// SB6-ANA-POP-02 — the body of a pop-out window: the widgets assigned to it.
//
// Adaptive:
//   - ONE widget  → it fills the window (the Phase-1 clean Viewer experience:
//     no grid chrome, no scrollbar), with a small floating move control.
//   - MULTIPLE    → a lean react-grid-layout (drag/resize) with a per-cell
//     header (drag handle + label + move control). Layout changes persist to
//     the window's record in the workspace store (session-only).
import GridLayout, { WidthProvider, type Layout } from "react-grid-layout";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import {
  usePopoutWorkspaceStore,
  POPOUT_GRID_COLS,
  type PopoutGridCell,
} from "./popoutWorkspaceStore.js";
import { MoveWidgetControl } from "./MoveWidgetControl.js";
import { WIDGET_LABEL, type PopoutWidgetKey } from "./windowSizes.js";
import { GripVerticalIcon } from "@aeriosiq/ui/icons";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const Grid = WidthProvider(GridLayout);
const ROW_HEIGHT = 30;
const MARGIN: [number, number] = [8, 8];
const DRAG_HANDLE_CLASS = "aero-popout-drag-handle";

export interface PopoutWidgetGridProps {
  windowId: string;
  layout: readonly PopoutGridCell[];
  renderWidget: (widget: PopoutWidgetKey) => React.ReactNode;
}

function sanitise(layout: readonly Layout[]): PopoutGridCell[] {
  return layout.map((l) => ({ i: l.i, x: l.x, y: l.y, w: l.w, h: l.h }));
}

export function PopoutWidgetGrid({
  windowId,
  layout,
  renderWidget,
}: PopoutWidgetGridProps): React.ReactElement {
  const setWindowLayout = usePopoutWorkspaceStore((s) => s.setWindowLayout);
  const widgets = layout.map((c) => c.i as PopoutWidgetKey);

  // Single-widget window → fill the viewport (Phase-1 behaviour), with a
  // small floating move/bring-back control so it isn't a dead end.
  const only = widgets.length === 1 ? widgets[0] : undefined;
  if (only) {
    return (
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
        {renderWidget(only)}
        <Box
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 20,
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <MoveWidgetControl widget={only} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "absolute", inset: 0, overflow: "auto" }}>
      <Grid
        className="aero-popout-workspace"
        layout={layout as Layout[]}
        cols={POPOUT_GRID_COLS}
        rowHeight={ROW_HEIGHT}
        margin={MARGIN}
        draggableHandle={`.${DRAG_HANDLE_CLASS}`}
        compactType={null}
        preventCollision
        onLayoutChange={(next) => {
          setWindowLayout(windowId, sanitise(next));
        }}
      >
        {widgets.map((key) => (
          <div key={key}>
            <Paper
              variant="outlined"
              role="region"
              aria-label={WIDGET_LABEL[key]}
              data-popout-widget-key={key}
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{
                  px: 1,
                  py: 0.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  flexShrink: 0,
                }}
              >
                <Tooltip title="Drag to move">
                  <Box
                    className={DRAG_HANDLE_CLASS}
                    role="button"
                    aria-label={`Move ${WIDGET_LABEL[key]}`}
                    tabIndex={0}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      width: 24,
                      height: 24,
                      cursor: "move",
                      color: "text.secondary",
                    }}
                  >
                    <GripVerticalIcon fontSize="small" />
                  </Box>
                </Tooltip>
                <Typography
                  component="h2"
                  sx={{
                    flexGrow: 1,
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  {WIDGET_LABEL[key]}
                </Typography>
                <MoveWidgetControl widget={key} />
              </Stack>
              <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto" }}>
                {renderWidget(key)}
              </Box>
            </Paper>
          </div>
        ))}
      </Grid>
    </Box>
  );
}
